import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { AuthState, UserClaims } from "@okta/okta-auth-js";
import { oktaAuth } from "./oktaConfig";

interface OktaContextValue {
  authState: AuthState | null;
  user: UserClaims | null;
  isAuthenticated: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string>;
}

const OktaContext = createContext<OktaContextValue | undefined>(undefined);

export function OktaProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState | null>(oktaAuth.authStateManager.getAuthState());
  const [user, setUser] = useState<UserClaims | null>(null);

  useEffect(() => {
    const handler = (state: AuthState) => {
      setAuthState(state);
      if (state.isAuthenticated) {
        oktaAuth.getUser().then(setUser).catch(() => setUser(null));
      } else {
        setUser(null);
      }
    };
    oktaAuth.authStateManager.subscribe(handler);
    oktaAuth.start();
    return () => {
      oktaAuth.authStateManager.unsubscribe(handler);
      oktaAuth.stop();
    };
  }, []);

  const signIn = useCallback(async () => {
    await oktaAuth.signInWithRedirect();
  }, []);

  const signOut = useCallback(async () => {
    await oktaAuth.signOut();
  }, []);

  const getAccessToken = useCallback(async (): Promise<string> => {
    const token = oktaAuth.getAccessToken();
    if (token) return token;
    const renewed = await oktaAuth.tokenManager.renew("accessToken");
    if (renewed && "accessToken" in renewed && typeof renewed.accessToken === "string") {
      return renewed.accessToken;
    }
    throw new Error("Unable to obtain Okta access token");
  }, []);

  const value = useMemo<OktaContextValue>(
    () => ({
      authState,
      user,
      isAuthenticated: !!authState?.isAuthenticated,
      signIn,
      signOut,
      getAccessToken,
    }),
    [authState, user, signIn, signOut, getAccessToken],
  );

  return <OktaContext.Provider value={value}>{children}</OktaContext.Provider>;
}

export function useOkta(): OktaContextValue {
  const ctx = useContext(OktaContext);
  if (!ctx) throw new Error("useOkta must be used inside <OktaProvider>");
  return ctx;
}
